import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DateTimeDisplay from './DateTimeDisplay';
import { API_ENDPOINTS } from '../config/api';

const API_BASE = `${API_ENDPOINTS.BASE_URL}/api`;

const ExpenseDashboard = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState({});
  const [budget, setBudget] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // New state for enhanced features
  const [pendingRequests, setPendingRequests] = useState([]);
  const [requestStats, setRequestStats] = useState({});
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharedUsers, setSharedUsers] = useState('');
  const [salaryStats, setSalaryStats] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  // Vendor management state
  const [vendors, setVendors] = useState([]);
  const [newVendorForm, setNewVendorForm] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    category: 'general',
    paymentTerms: 'net_30',
    notes: ''
  });

  // Budget settings state
  const [budgets, setBudgets] = useState([]);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [budgetForm, setBudgetForm] = useState({
    year: new Date().getFullYear(),
    quarter: '',
    month: '',
    totalBudget: '',
    categories: {}
  });

  // Filters
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    // Check if admin is logged in
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) {
      navigate('/admin');
      return;
    }

    loadData();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadExpenses(),
        loadStats(),
        loadBudget(),
        loadCategories(),
        loadPendingRequests(),
        loadRequestStats(),
        loadSalaryStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExpenses = async () => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await fetch(`${API_BASE}/expenses${queryParams ? `?${queryParams}` : ''}`);
      if (response.ok) {
        const data = await response.json();
        setExpenses(data);
      } else {
        console.error('Failed to load expenses:', response.status);
      }
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/expenses/reports/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadBudget = async () => {
    try {
      const response = await fetch(`${API_BASE}/expenses/budgets/active`);
      if (response.ok) {
        const data = await response.json();
        setBudget(data);
      }
    } catch (error) {
      console.error('Error loading budget:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/expenses/categories/all`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadPendingRequests = async () => {
    try {
      const response = await fetch(`${API_BASE}/expenses/requests/pending`);
      if (response.ok) {
        const data = await response.json();
        setPendingRequests(data);
      }
    } catch (error) {
      console.error('Error loading pending requests:', error);
    }
  };

  const loadRequestStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/expenses/requests/stats`);
      if (response.ok) {
        const data = await response.json();
        setRequestStats(data);
      }
    } catch (error) {
      console.error('Error loading request stats:', error);
    }
  };

  const loadSalaryStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/expenses/salary-expenses/stats`);
      if (response.ok) {
        const data = await response.json();
        setSalaryStats(data);
      }
    } catch (error) {
      console.error('Error loading salary stats:', error);
    }
  };

  // Vendor management functions
  const loadVendors = async () => {
    try {
      const response = await fetch(`${API_BASE}/expenses/vendors/all`);
      if (response.ok) {
        const data = await response.json();
        setVendors(data);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const addVendor = async () => {
    if (!newVendorForm.name || !newVendorForm.email) {
      alert('Vendor name and email are required');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/expenses/vendors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newVendorForm)
      });

      if (response.ok) {
        alert('Vendor added successfully!');
        setNewVendorForm({
          name: '',
          contactPerson: '',
          email: '',
          phone: '',
          address: '',
          category: 'general',
          paymentTerms: 'net_30',
          notes: ''
        });
        loadVendors(); // Refresh the list
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to add vendor');
      }
    } catch (error) {
      console.error('Error adding vendor:', error);
      alert('Failed to add vendor. Please try again.');
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    // Reload expenses with new filters
    setTimeout(loadExpenses, 100);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      approved: 'bg-green-100 text-green-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority) => {
    if (priority >= 8) return 'bg-red-100 text-red-800';
    if (priority >= 5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  // Collaboration functions
  const loadComments = async (expenseId) => {
    try {
      const response = await fetch(`${API_BASE}/expenses/${expenseId}/comments`);
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedExpense) return;

    try {
      const response = await fetch(`${API_BASE}/expenses/${selectedExpense.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comment: newComment,
          commentedBy: 'Admin User' // In real app, get from auth
        })
      });

      if (response.ok) {
        setNewComment('');
        loadComments(selectedExpense.id);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleShareExpense = async () => {
    if (!sharedUsers.trim() || !selectedExpense) return;

    try {
      const users = sharedUsers.split(',').map(user => user.trim());
      const response = await fetch(`${API_BASE}/expenses/${selectedExpense.id}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sharedWith: users,
          sharedBy: 'Admin User' // In real app, get from auth
        })
      });

      if (response.ok) {
        setSharedUsers('');
        setShowShareModal(false);
        alert('Expense shared successfully!');
      }
    } catch (error) {
      console.error('Error sharing expense:', error);
    }
  };

  const handleBulkAction = async (action, expenseIds) => {
    try {
      const response = await fetch(`${API_BASE}/expenses/requests/bulk-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          expenseIds,
          approvedBy: 'Admin User', // In real app, get from auth
          rejectionReason: action === 'reject' ? 'Bulk rejection' : undefined
        })
      });

      if (response.ok) {
        loadData(); // Refresh all data
        alert(`${expenseIds.length} expenses ${action}d successfully!`);
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  };

  const handleApproveExpense = async (expenseId) => {
    try {
      const response = await fetch(`${API_BASE}/expenses/${expenseId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approvedBy: 'Admin User' })
      });

      if (response.ok) {
        loadData();
        alert('Expense approved successfully!');
      }
    } catch (error) {
      console.error('Error approving expense:', error);
    }
  };

  const handleRejectExpense = async (expenseId, reason) => {
    try {
      const response = await fetch(`${API_BASE}/expenses/${expenseId}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rejectedBy: 'Admin User',
          rejectionReason: reason
        })
      });

      if (response.ok) {
        loadData();
        alert('Expense rejected successfully!');
      }
    } catch (error) {
      console.error('Error rejecting expense:', error);
    }
  };

  const generateSalaryExpenses = async () => {
    const month = new Date().toISOString().slice(0, 7).split('-')[1];
    const year = new Date().getFullYear();

    try {
      const response = await fetch(`${API_BASE}/expenses/generate-salary-expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, year })
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        loadData();
      }
    } catch (error) {
      console.error('Error generating salary expenses:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading expense dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900">Enhanced Expense Management Dashboard</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowNotificationModal(true)}
              className="relative bg-gray-100 text-gray-600 p-2 rounded-full hover:bg-gray-200 transition duration-300"
              title="Notifications"
            >
              🔔
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {notifications.filter(n => !n.read).length}
                </span>
              )}
            </button>
            <button
              onClick={generateSalaryExpenses}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition duration-300"
            >
              Generate Salary Expenses
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition duration-300"
            >
              Back to Dashboard
            </button>
            <DateTimeDisplay />
          </div>
        </div>

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">{stats.totalExpenses || 0}</div>
            <div className="text-sm text-gray-600">Total Expenses</div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-yellow-600">{requestStats.pending || 0}</div>
            <div className="text-sm text-gray-600">Pending Approval</div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-red-600">{requestStats.urgent || 0}</div>
            <div className="text-sm text-gray-600">Urgent Requests</div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(stats.totalAmount || 0)}
            </div>
            <div className="text-sm text-gray-600">Total Spent</div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(salaryStats.totalAmount || 0)}
            </div>
            <div className="text-sm text-gray-600">Salary Expenses</div>
          </div>
        </div>

        {/* Budget Overview */}
        {budget && (
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 mb-8">
            <h2 className="text-xl font-semibold text-blue-800 mb-4">Budget Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(budget.totalAllocated)}
                </div>
                <div className="text-sm text-gray-600">Total Allocated</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(budget.totalSpent)}
                </div>
                <div className="text-sm text-gray-600">Total Spent</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {formatCurrency(budget.totalRemaining)}
                </div>
                <div className="text-sm text-gray-600">Remaining</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${budget.totalAllocated > 0 ? (budget.totalSpent / budget.totalAllocated) * 100 : 0}%` }}
                ></div>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {budget.totalAllocated > 0 ? ((budget.totalSpent / budget.totalAllocated) * 100).toFixed(1) : 0}% utilized
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Tab Navigation */}
        <div className="flex gap-2 bg-white border border-slate-200 rounded-xl shadow-sm p-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-bold text-sm rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'overview'
                ? 'bg-slate-950 text-white shadow'
                : 'text-slate-600 bg-slate-50 hover:bg-amber-50 hover:text-amber-800'
            }`}
          >
            Stats Overview
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-6 py-3 font-bold text-sm rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'requests'
                ? 'bg-slate-950 text-white shadow'
                : 'text-slate-600 bg-slate-50 hover:bg-amber-50 hover:text-amber-800'
            }`}
          >
            Applications Requests ({requestStats.pending || 0})
          </button>
          <button
            onClick={() => setActiveTab('expenses')}
            className={`px-6 py-3 font-bold text-sm rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'expenses'
                ? 'bg-slate-950 text-white shadow'
                : 'text-slate-600 bg-slate-50 hover:bg-amber-50 hover:text-amber-800'
            }`}
          >
            💰 All Expenses ({expenses.length})
          </button>
          <button
            onClick={() => setActiveTab('salaries')}
            className={`px-6 py-3 font-bold text-sm rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'salaries'
                ? 'bg-slate-950 text-white shadow'
                : 'text-slate-600 bg-slate-50 hover:bg-amber-50 hover:text-amber-800'
            }`}
          >
            💼 Salaries ({salaryStats.totalSalaryExpenses || 0})
          </button>

          <button
            onClick={() => setActiveTab('reports')}
            className={`px-6 py-3 font-bold text-sm rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'reports'
                ? 'bg-slate-950 text-white shadow'
                : 'text-slate-600 bg-slate-50 hover:bg-amber-50 hover:text-amber-800'
            }`}
          >
            Growth Reports
          </button>
          <button
            onClick={() => {
              setActiveTab('vendors');
              loadVendors();
            }}
            className={`px-6 py-3 font-bold text-sm rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'vendors'
                ? 'bg-slate-950 text-white shadow'
                : 'text-slate-600 bg-slate-50 hover:bg-amber-50 hover:text-amber-800'
            }`}
          >
            Facilities Vendors ({vendors.length})
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-6 py-3 font-bold text-sm rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'settings'
                ? 'bg-slate-950 text-white shadow'
                : 'text-slate-600 bg-slate-50 hover:bg-amber-50 hover:text-amber-800'
            }`}
          >
            Settings Settings
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Expenses */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Recent Expenses</h2>
              <div className="space-y-4">
                {expenses.slice(0, 5).map((expense) => (
                  <div key={expense.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-800">{expense.description}</h4>
                      <p className="text-sm text-gray-600">{expense.vendor} ? {expense.date}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600">{formatCurrency(expense.amount)}</div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(expense.status)}`}>
                        {expense.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Category Breakdown</h2>
              <div className="space-y-4">
                {Object.entries(stats.categoryBreakdown || {}).map(([category, amount]) => (
                  <div key={category} className="flex justify-between items-center">
                    <span className="text-gray-700 capitalize">{category.replace('_', ' ')}</span>
                    <span className="font-bold text-green-600">{formatCurrency(amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Requests Dashboard Tab */}
        {activeTab === 'requests' && (
          <div className="space-y-6">
            {/* Bulk Actions */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Bulk Actions</h2>
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    const urgentIds = pendingRequests.filter(r => r.priority >= 8).map(r => r.id);
                    if (urgentIds.length > 0) {
                      handleBulkAction('approve', urgentIds);
                    }
                  }}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition duration-300"
                >
                  Approve Urgent ({requestStats.urgent || 0})
                </button>
                <button
                  onClick={() => {
                    const pendingIds = pendingRequests.map(r => r.id);
                    if (pendingIds.length > 0) {
                      handleBulkAction('approve', pendingIds);
                    }
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
                >
                  Approve All Pending ({requestStats.pending || 0})
                </button>
              </div>
            </div>

            {/* Pending Requests */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Pending Approval Requests</h2>
              <div className="space-y-4">
                {pendingRequests.map((request) => (
                  <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-800">{request.description}</h3>
                        <p className="text-sm text-gray-600">
                          {request.vendor} ? {request.category.replace('_', ' ')} ? Submitted {request.daysPending} days ago
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-600 text-lg">{formatCurrency(request.amount)}</div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityBadge(request.priority)}`}>
                          Priority: {request.priority}/10
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleApproveExpense(request.id)}
                        className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 transition duration-300"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          const reason = prompt('Rejection reason:');
                          if (reason) handleRejectExpense(request.id, reason);
                        }}
                        className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700 transition duration-300"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => {
                          setSelectedExpense(request);
                          setShowCommentModal(true);
                          loadComments(request.id);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition duration-300"
                      >
                        💬 Comments ({request.comments?.length || 0})
                      </button>
                      <button
                        onClick={() => {
                          setSelectedExpense(request);
                          setShowShareModal(true);
                        }}
                        className="bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700 transition duration-300"
                      >
                        Students Share
                      </button>
                    </div>
                  </div>
                ))}
                {pendingRequests.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No pending approval requests</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Expenses Tab */}
        {activeTab === 'expenses' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-blue-800">All Expenses</h2>
              <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300">
                Add Expense
              </button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange({ category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange({ status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="approved">Approved</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="rejected">Rejected</option>
              </select>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange({ startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Start Date"
              />
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange({ endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="End Date"
              />
            </div>

            {/* Expenses Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Description</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Category</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Vendor</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Amount</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense) => (
                    <tr key={expense.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">{expense.description}</td>
                      <td className="py-3 px-4 capitalize">{expense.category.replace('_', ' ')}</td>
                      <td className="py-3 px-4">{expense.vendor}</td>
                      <td className="py-3 px-4 font-bold text-green-600">{formatCurrency(expense.amount)}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(expense.status)}`}>
                          {expense.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4">{new Date(expense.date).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedExpense(expense);
                              setShowCommentModal(true);
                              loadComments(expense.id);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                            title="Comments"
                          >
                            💬 ({expense.comments?.length || 0})
                          </button>
                          <button
                            onClick={() => {
                              setSelectedExpense(expense);
                              setShowShareModal(true);
                            }}
                            className="text-purple-600 hover:text-purple-800 text-sm"
                            title="Share"
                          >
                            Students
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Salaries Tab */}
        {activeTab === 'salaries' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="text-2xl font-bold text-blue-600">{salaryStats.totalSalaryExpenses || 0}</div>
                <div className="text-sm text-gray-600">Total Salary Expenses</div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="text-2xl font-bold text-green-600">{formatCurrency(salaryStats.totalAmount || 0)}</div>
                <div className="text-sm text-gray-600">Total Salary Amount</div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="text-2xl font-bold text-purple-600">
                  {salaryStats.teacherSalaries || 0} Teachers, {salaryStats.staffSalaries || 0} Staff
                </div>
                <div className="text-sm text-gray-600">Employee Breakdown</div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Monthly Salary Breakdown</h2>
              <div className="space-y-4">
                {Object.entries(salaryStats.monthlyBreakdown || {}).map(([month, amount]) => (
                  <div key={month} className="flex justify-between items-center">
                    <span className="text-gray-700">{month}</span>
                    <span className="font-bold text-green-600">{formatCurrency(amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}



        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Advanced Expense Reports</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-800 mb-2">Monthly Trends</h3>
                  <div className="space-y-2">
                    {Object.entries(stats.monthlyTrend || {}).map(([month, amount]) => (
                      <div key={month} className="flex justify-between">
                        <span className="text-sm text-gray-600">{month}</span>
                        <span className="text-sm font-medium">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-2">Category Analysis</h3>
                  <div className="space-y-2">
                    {Object.entries(stats.categoryBreakdown || {}).map(([category, amount]) => (
                      <div key={category} className="flex justify-between">
                        <span className="text-sm text-gray-600 capitalize">{category.replace('_', ' ')}</span>
                        <span className="text-sm font-medium">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Vendors Tab */}
        {activeTab === 'vendors' && (
          <div className="space-y-6">
            {/* Add New Vendor Form */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Add New Vendor</h2>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!newVendorForm.name || !newVendorForm.email) {
                  alert('Vendor name and email are required');
                  return;
                }
                await addVendor();
              }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <input
                  type="text"
                  placeholder="Vendor Name *"
                  value={newVendorForm.name}
                  onChange={(e) => setNewVendorForm({...newVendorForm, name: e.target.value})}
                  className="border rounded px-3 py-2"
                  required
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={newVendorForm.email}
                  onChange={(e) => setNewVendorForm({...newVendorForm, email: e.target.value})}
                  className="border rounded px-3 py-2"
                  required
                />
                <input
                  type="text"
                  placeholder="Contact Person"
                  value={newVendorForm.contactPerson}
                  onChange={(e) => setNewVendorForm({...newVendorForm, contactPerson: e.target.value})}
                  className="border rounded px-3 py-2"
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={newVendorForm.phone}
                  onChange={(e) => setNewVendorForm({...newVendorForm, phone: e.target.value})}
                  className="border rounded px-3 py-2"
                />
                <select
                  value={newVendorForm.category}
                  onChange={(e) => setNewVendorForm({...newVendorForm, category: e.target.value})}
                  className="border rounded px-3 py-2"
                >
                  <option value="general">General</option>
                  <option value="supplies">Supplies</option>
                  <option value="services">Services</option>
                  <option value="technology">Technology</option>
                  <option value="utilities">Utilities</option>
                  <option value="catering">Catering</option>
                  <option value="transport">Transport</option>
                  <option value="educational">Educational</option>
                  <option value="maintenance">Maintenance</option>
                </select>
                <select
                  value={newVendorForm.paymentTerms}
                  onChange={(e) => setNewVendorForm({...newVendorForm, paymentTerms: e.target.value})}
                  className="border rounded px-3 py-2"
                >
                  <option value="net_15">Net 15 Days</option>
                  <option value="net_30">Net 30 Days</option>
                  <option value="net_45">Net 45 Days</option>
                  <option value="net_60">Net 60 Days</option>
                  <option value="cod">Cash on Delivery</option>
                </select>
                <textarea
                  placeholder="Address"
                  value={newVendorForm.address}
                  onChange={(e) => setNewVendorForm({...newVendorForm, address: e.target.value})}
                  className="border rounded px-3 py-2 md:col-span-2"
                  rows="2"
                />
                <textarea
                  placeholder="Notes"
                  value={newVendorForm.notes}
                  onChange={(e) => setNewVendorForm({...newVendorForm, notes: e.target.value})}
                  className="border rounded px-3 py-2 md:col-span-3"
                  rows="2"
                />
                <div className="md:col-span-3">
                  <button
                    type="submit"
                    className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
                  >
                    Add Vendor
                  </button>
                </div>
              </form>
            </div>

            {/* Existing Vendors List */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Existing Vendors ({vendors.length})</h2>
              {vendors.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No vendors found.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {vendors.map((vendor) => (
                    <div key={vendor.id} className="border rounded-lg p-4 bg-white shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-800 text-lg">{vendor.name}</h4>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`px-2 py-1 rounded text-xs ${
                              vendor.category === 'supplies' ? 'bg-blue-100 text-blue-800' :
                              vendor.category === 'services' ? 'bg-green-100 text-green-800' :
                              vendor.category === 'technology' ? 'bg-purple-100 text-purple-800' :
                              vendor.category === 'utilities' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {vendor.category}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${vendor.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {vendor.status}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="text-sm text-gray-600 space-y-1">
                        <p><strong>Email:</strong> {vendor.email}</p>
                        {vendor.contactPerson && <p><strong>Contact:</strong> {vendor.contactPerson}</p>}
                        {vendor.phone && <p><strong>Phone:</strong> {vendor.phone}</p>}
                        <p><strong>Payment Terms:</strong> {vendor.paymentTerms.replace('_', ' ')}</p>
                        {vendor.address && <p><strong>Address:</strong> {vendor.address}</p>}
                        {vendor.notes && <p><strong>Notes:</strong> {vendor.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Budget Management */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-blue-800">Budget Settings</h2>
                <button
                  onClick={() => setShowBudgetForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
                >
                  Create New Budget
                </button>
              </div>

              {/* Current Active Budget */}
              {budget ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-green-800">Active Budget</h3>
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm font-medium">
                      {budget.year} {budget.quarter ? `Q${budget.quarter}` : budget.month ? `Month ${budget.month}` : 'Annual'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-xl font-bold text-green-600">{formatCurrency(budget.totalAllocated)}</div>
                      <div className="text-gray-600">Total Allocated</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-600">{formatCurrency(budget.totalSpent)}</div>
                      <div className="text-gray-600">Total Spent</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-purple-600">{formatCurrency(budget.totalRemaining)}</div>
                      <div className="text-gray-600">Remaining</div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${budget.totalAllocated > 0 ? (budget.totalSpent / budget.totalAllocated) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <div className="text-sm text-gray-600 mt-1 text-center">
                      {budget.totalAllocated > 0 ? ((budget.totalSpent / budget.totalAllocated) * 100).toFixed(1) : 0}% utilized
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-center">
                  <p className="text-yellow-800">No active budget found. Create a budget to track expenses.</p>
                </div>
              )}

              {/* Category Allocations */}
              {budget && budget.categories && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Category Allocations</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(budget.categories).map(([categoryId, allocation]) => {
                      const category = categories.find(cat => cat.id === categoryId);
                      const categoryName = category ? category.name : categoryId.replace('_', ' ');
                      const spent = allocation.spent || 0;
                      const allocated = allocation.allocated || 0;
                      const utilization = allocated > 0 ? (spent / allocated) * 100 : 0;

                      return (
                        <div key={categoryId} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-medium text-gray-800">{categoryName}</h4>
                            <span className={`px-2 py-1 rounded text-xs ${
                              utilization > 90 ? 'bg-red-100 text-red-800' :
                              utilization > 75 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {utilization.toFixed(1)}%
                            </span>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Allocated:</span>
                              <span className="font-medium">{formatCurrency(allocated)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Spent:</span>
                              <span className="font-medium text-blue-600">{formatCurrency(spent)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Remaining:</span>
                              <span className="font-medium text-green-600">{formatCurrency(allocated - spent)}</span>
                            </div>
                          </div>
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${
                                  utilization > 90 ? 'bg-red-600' :
                                  utilization > 75 ? 'bg-yellow-600' :
                                  'bg-green-600'
                                }`}
                                style={{ width: `${Math.min(utilization, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Budget History */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Budget History</h2>
              <div className="text-center text-gray-500 py-8">
                <p>Budget history feature coming soon...</p>
                <p className="text-sm mt-2">This will show all previous budgets and their performance.</p>
              </div>
            </div>
          </div>
        )}

        {/* Comment Modal */}
        {showCommentModal && selectedExpense && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-blue-800">Comments - {selectedExpense.description}</h3>
                <button
                  onClick={() => setShowCommentModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4 mb-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-gray-800">{comment.commentedBy}</span>
                      <span className="text-sm text-gray-500">
                        {new Date(comment.commentedAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700">{comment.comment}</p>
                  </div>
                ))}
              </div>

              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <button
                  onClick={handleAddComment}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
                >
                  Add Comment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && selectedExpense && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-blue-800">Share Expense</h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <p className="text-gray-600 mb-4">
                Share "{selectedExpense.description}" with other users. Enter email addresses separated by commas.
              </p>

              <textarea
                value={sharedUsers}
                onChange={(e) => setSharedUsers(e.target.value)}
                placeholder="user1@example.com, user2@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                rows="3"
              />

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShareExpense}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 transition duration-300"
                >
                  Share
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notification Modal */}
        {showNotificationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-blue-800">Notifications</h3>
                <button
                  onClick={() => setShowNotificationModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                {notifications.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No notifications</p>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`border rounded-lg p-4 ${
                        !notification.read ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-gray-800">{notification.title}</h4>
                        <div className="flex items-center space-x-2">
                          {!notification.read && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded">New</span>
                          )}
                          <span className="text-sm text-gray-500">
                            {new Date(notification.createdAt).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-3">{notification.message}</p>
                      {!notification.read && (
                        <button
                          onClick={async () => {
                            try {
                              await fetch(`${API_BASE}/expenses/notifications/${notification.id}/read`, {
                                method: 'PUT'
                              });
                              setNotifications(prev =>
                                prev.map(n =>
                                  n.id === notification.id ? { ...n, read: true } : n
                                )
                              );
                            } catch (error) {
                              console.error('Error marking notification as read:', error);
                            }
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm underline"
                        >
                          Mark as read
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Budget Form Modal */}
        {showBudgetForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Create New Budget</h2>
                  <button
                    onClick={() => setShowBudgetForm(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={async (e) => {
                  e.preventDefault();

                  if (!budgetForm.totalBudget || parseFloat(budgetForm.totalBudget) <= 0) {
                    alert('Please enter a valid total budget amount');
                    return;
                  }

                  // Prepare budget data
                  const totalBudget = parseFloat(budgetForm.totalBudget);
                  const budgetData = {
                    year: parseInt(budgetForm.year),
                    quarter: budgetForm.quarter || null,
                    month: budgetForm.month || null,
                    categories: {}
                  };

                  // Distribute budget equally across categories
                  const categoryCount = categories.length;
                  const equalAllocation = Math.floor(totalBudget / categoryCount);
                  const remainder = totalBudget % categoryCount;

                  categories.forEach((category, index) => {
                    // Add remainder to first category to ensure total adds up
                    const allocation = index === 0 ? equalAllocation + remainder : equalAllocation;
                    budgetData.categories[category.id] = {
                      allocated: allocation,
                      spent: 0,
                      remaining: allocation
                    };
                  });

                  try {
                    const response = await fetch(`${API_BASE}/expenses/budgets`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(budgetData)
                    });

                    if (response.ok) {
                      alert(`Budget created successfully! Total: ${formatCurrency(totalBudget)} distributed across ${categoryCount} categories.`);
                      setShowBudgetForm(false);
                      setBudgetForm({
                        year: new Date().getFullYear(),
                        quarter: '',
                        month: '',
                        totalBudget: '',
                        categories: {}
                      });
                      loadBudget(); // Refresh budget data
                    } else {
                      const error = await response.json();
                      alert(error.error || 'Failed to create budget');
                    }
                  } catch (error) {
                    console.error('Error creating budget:', error);
                    alert('Failed to create budget. Please try again.');
                  }
                }}>
                  {/* Budget Period Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Year <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={budgetForm.year}
                        onChange={(e) => setBudgetForm({...budgetForm, year: parseInt(e.target.value)})}
                        min="2020"
                        max="2030"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quarter (Optional)
                      </label>
                      <select
                        value={budgetForm.quarter}
                        onChange={(e) => setBudgetForm({...budgetForm, quarter: e.target.value, month: ''})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Quarter</option>
                        <option value="1">Q1 (Jan-Mar)</option>
                        <option value="2">Q2 (Apr-Jun)</option>
                        <option value="3">Q3 (Jul-Sep)</option>
                        <option value="4">Q4 (Oct-Dec)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Month (Optional)
                      </label>
                      <select
                        value={budgetForm.month}
                        onChange={(e) => setBudgetForm({...budgetForm, month: e.target.value, quarter: ''})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Month</option>
                        <option value="1">January</option>
                        <option value="2">February</option>
                        <option value="3">March</option>
                        <option value="4">April</option>
                        <option value="5">May</option>
                        <option value="6">June</option>
                        <option value="7">July</option>
                        <option value="8">August</option>
                        <option value="9">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Total Budget Amount <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={budgetForm.totalBudget}
                        onChange={(e) => setBudgetForm({...budgetForm, totalBudget: e.target.value})}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      {budgetForm.totalBudget && (
                        <p className="text-sm text-gray-500 mt-1">
                          {formatCurrency(parseFloat(budgetForm.totalBudget) || 0)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-blue-800 text-sm">
                      <strong>Note:</strong> This will create a new budget and automatically deactivate any existing active budget.
                      You can allocate funds to categories after creation by editing the budget.
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowBudgetForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Create Budget
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseDashboard;
